import React from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import { Button } from '@/components/ui/button';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

const heroImages = [
  'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/6x7l7fco_local-concrete-pavers-near-me.jpg',
  'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/crivqsf0_PAIVER-55.jpg',
  'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/7i6mornw_gallery-a45-IMG_2282.jpg',
  'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/lcrljq7n_bedrock-decorative-concrete-pavers-768x512.jpg',
  'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/nbpt5kl5_Elis-Paver-Patios-2450-Mottman-Rd-SW-Tumwater-98512-Washington-United-States-360-807-4198-paver-installations-900x900.jpg',
  'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/dj2bkvd6_istockphoto-623298654-612x612.jpg',
  'https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/c03q115u_mm-dual-color-circular-patio-with-seating-wall.jpg',
];

export const HeroSection = ({ onOpenLeadModal }) => {
  const { t } = useTranslation();

  return (
    <section className="hero-section" data-testid="hero-section">
      {/* Background Slider */}
      <div className="absolute inset-0 z-0">
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          effect="fade"
          autoplay={{
            delay: 6000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
          }}
          loop={true}
          className="w-full h-full"
        >
          {heroImages.map((image, index) => (
            <SwiperSlide key={index}>
              <div
                className="w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${image})` }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30 z-10" />
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          {/* Logo Badge - Standalone, larger, moved up, hidden on mobile */}
          <div className="mb-12 fade-in opacity-0 hidden md:block" style={{ marginTop: '-2rem' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_remodel-pros-1/artifacts/66uykm11_logo%20edx%20para%20site.png" 
              alt="EDX Construction" 
              className="h-28 sm:h-32 lg:h-36 w-auto mx-auto drop-shadow-2xl"
              style={{ maxWidth: '400px', filter: 'drop-shadow(0 15px 40px rgba(0,0,0,0.6))' }}
            />
          </div>

          {/* Main Headline */}
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight fade-in-up delay-100 opacity-0"
            style={{ fontFamily: 'Outfit' }}
            data-testid="hero-title"
          >
            {t('hero.title')}
          </h1>

          {/* Subheadline */}
          <p
            className="text-xl sm:text-2xl text-white/95 mb-10 leading-relaxed font-medium fade-in-up delay-200 opacity-0"
            style={{ fontFamily: 'IBM Plex Sans' }}
            data-testid="hero-subtitle"
          >
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 fade-in-up delay-300 opacity-0">
            <Button
              onClick={onOpenLeadModal}
              className="btn-premium btn-premium-primary text-lg px-10 py-6"
              data-testid="hero-cta-button"
            >
              {t('hero.cta')}
            </Button>
            <Button
              onClick={() => document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-premium bg-white/10 text-white border-2 border-white hover:bg-white hover:text-[hsl(215,75%,16%)] text-lg px-10 py-6 backdrop-blur-sm"
            >
              {t('hero.viewServices')}
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 flex flex-wrap gap-8 items-center fade-in-up delay-400 opacity-0">
            <div className="flex items-center space-x-2">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-[hsl(224,64%,33%)] border-2 border-white flex items-center justify-center">
                    <span className="text-white text-xs font-bold">★</span>
                  </div>
                ))}
              </div>
              <span className="text-white font-semibold text-sm">{t('hero.rating')}</span>
            </div>
            <div className="h-8 w-px bg-white/30" />
            <div className="text-white">
              <p className="font-bold text-2xl">500+</p>
              <p className="text-sm text-white/80">{t('hero.projectsCompleted')}</p>
            </div>
            <div className="h-8 w-px bg-white/30" />
            <div className="text-white">
              <p className="font-bold text-2xl">10+</p>
              <p className="text-sm text-white/80">{t('hero.yearsExperience')}</p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 fade-in-up delay-500 opacity-0 hidden md:block">
          <div className="flex flex-col items-center text-white/70 animate-bounce">
            <span className="text-xs uppercase tracking-wider mb-2 font-semibold">Scroll to Explore</span>
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};